import { useEffect, useState } from "react";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { useDispatch, useSelector } from "react-redux";
import { selectIsWishlistsLoading, selectWishlists } from "~/features/wishlist/redux/wishlistSelectors";
import { type WishlistModel } from "~/features/wishlist/types/model";
import { addToWishlistThunk } from "~/features/wishlist/redux/wishlistSlice";
import WishlistDropdown from "~/features/wishlist/components/WishlistDropdown";
import OptimeSolutionsMessage from "~/domains/optime/components/OptimeSolutionsMessage";
import { Trans, useTranslation } from "react-i18next";
import { isKITProduct } from "~/domains/product/types/ProductData";
import { ProductService } from "~/features/addToCart/api/ProductService";

import { Button, Stack, TextField } from "~/common/mui";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { Alert } from "~/common/mui/Alert/Alert";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import {
  AtcContentLayout,
  AtcMainAreaLayout,
} from "~/features/addToCart/components/AddToCartProcess/shell/AtcContentLayout";
import { AtcProductDetails } from "~/features/addToCart/components/AddToCartProcess/shell/AtcProductDetails";
import { LoadingScreen } from "~/features/addToCart/components/AddToCartProcess/Modals/LoadingScreen";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { Controller, useForm } from "react-hook-form";
import { getNetPrice, getPackageByCode } from "~/domains/product/utils/productDescriptorUtils";
import { PriceBlock } from "~/features/productDetails/components/ProductSummary/components/PdpPriceBlock";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import { canSeePriceRefColumn } from "~/features/quickEntry/utils/helpers";
import { AtcDialogShell } from "~/features/addToCart/components/AddToCartProcess/shell/AtcDialogShell";
import { getFirstValidPackCode } from "./utils";
import BookmarkIcon from "@mui/icons-material/Bookmark";

export type AddToWishlistTrackingCb = (descriptor: ProductDescriptor) => void;

export interface AddToWishlistWizardProps {
  productCode: string;
  initialQuantity: number;
  initialPriceReference?: string;
  selectedOptionalParts?: {
    productCode: string;
    quantity: number;
  }[];
  closePopup: () => void;
  addTracking?: AddToWishlistTrackingCb;
}

export enum AddToWishlistStep {
  LOADING,
  HAS_WISHLISTS,
  CREATING_WISHLIST,
  POST_SENDING_SUCCESS,
  POST_SENDING_ERROR,
}

function getInitialStep(
  product: ProductDescriptor,
  isWishlistsLoading: boolean,
  availableWishlists: WishlistModel[],
): AddToWishlistStep {
  if (isWishlistsLoading) {
    return AddToWishlistStep.LOADING;
  }
  if (availableWishlists.length === 0) {
    return AddToWishlistStep.CREATING_WISHLIST;
  }
  return AddToWishlistStep.HAS_WISHLISTS;
}

interface ATWFormData {
  wishlistName: string;
  wishlistId: string;
  quantity: number;
  priceReference: string;
}

export const AddToWishlistWizard = (props: AddToWishlistWizardProps) => {
  const { selection, status } = usePriceReferenceSelection();
  const formProps = useForm<ATWFormData>({
    defaultValues: {
      wishlistName: "",
      wishlistId: "",
      quantity: props.initialQuantity,
      priceReference: props.initialPriceReference ?? getInitialPriceReference(selection),
    },
  });

  const product = ProductService.useProductDescriptor({
    productCode: props.productCode,
    priceReference: formProps.watch("priceReference"),
    options: { fetch: true },
  });

  const isWishlistsLoading = useSelector(selectIsWishlistsLoading);

  useEffect(() => {
    if (step === AddToWishlistStep.LOADING && !isWishlistsLoading) {
      setStep(getInitialStep(product, isWishlistsLoading, availableWishlists));
    }
  }, [isWishlistsLoading]);

  const availableWishlists = useSelector(selectWishlists);

  const [step, setStep] = useState(() => getInitialStep(product, isWishlistsLoading, availableWishlists));
  const { t } = useTranslation();
  const { closePopup, selectedOptionalParts } = props;

  const [inFlight, setInFlight] = useState(false);

  const dispatch = useDispatch();
  const addToWishlistCb = async ({ wishlistId, wishlistName, quantity, priceReference }: ATWFormData) => {
    if (status !== "ready") {
      return;
    }
    if (props.addTracking) {
      props.addTracking(product);
    }

    let optionalParts;
    if (selectedOptionalParts) {
      optionalParts = selectedOptionalParts;
    } else if (isKITProduct(product)) {
      optionalParts = product.base.optionalParts.map((item) => ({
        productCode: item.product.code,
        quantity: item.quantity * quantity,
      }));
    }
    setInFlight(true);
    try {
      await dispatch(
        addToWishlistThunk({
          quantity,
          wishlistName,
          productCode: product.base.code,
          id: wishlistId ?? undefined,
          priceReference,
          optionalParts,
        }),
      );
      setStep(AddToWishlistStep.POST_SENDING_SUCCESS);
    } catch {
      setStep(AddToWishlistStep.POST_SENDING_ERROR);
    } finally {
      setInFlight(false);
    }
  };

  const notificationSlot = product.base.isOptime && <OptimeSolutionsMessage />;
  const packCode = getFirstValidPackCode(product);
  const selectedPack = getPackageByCode(product, packCode);
  const packSize = selectedPack?.packSize ?? 1;
  const isExternalProduct = product.base.externalProduct;
  const salePriceEnabled = false;

  const priceSlot = (
    <Stack gap={2}>
      <ATWPriceBlock
        packCode={packCode}
        descriptor={product}
        priceReference={formProps.watch("priceReference")}
        quantity={formProps.watch("quantity")}
        salePriceEnabled={salePriceEnabled}
      />

      {!isExternalProduct && (
        <Controller
          control={formProps.control}
          render={({ field: { value, onChange } }) => (
            <PriceReferenceParam
              priceReference={value}
              externalLabel={true}
              product={product.base}
              editable={canSeePriceRefColumn(selection)}
              disabled={salePriceEnabled}
              onChange={(value) => onChange(value ?? "")}
            />
          )}
          name={"priceReference"}
        />
      )}

      <Controller
        control={formProps.control}
        render={({ field: { value, onChange, onBlur }, fieldState }) => (
          <QuantityInput
            variant="compact"
            onValueChange={onChange}
            value={value}
            onBlur={onBlur}
            step={packSize}
            error={!!fieldState.error}
          />
        )}
        name={"quantity"}
      />
    </Stack>
  );

  let content = null;
  switch (step) {
    case AddToWishlistStep.LOADING: {
      content = <LoadingScreen />;
      break;
    }
    case AddToWishlistStep.HAS_WISHLISTS:
    case AddToWishlistStep.CREATING_WISHLIST: {
      let innestContent = null;
      if (step === AddToWishlistStep.HAS_WISHLISTS) {
        innestContent = (
          <Controller
            control={formProps.control}
            name="wishlistId"
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <WishlistDropdown
                wishlists={availableWishlists}
                onWishlistSelect={(wishlistId) => {
                  onChange(wishlistId);
                  const wishlist = availableWishlists.find((wishlist) => wishlist.id === wishlistId);
                  if (wishlist) {
                    formProps.setValue("wishlistName", wishlist.name);
                  }
                }}
                onNewWishlist={() => {
                  onChange("");
                  setStep(AddToWishlistStep.CREATING_WISHLIST);
                }}
                selectedWishlist={value}
                error={!!error}
              />
            )}
          />
        );
      } else {
        innestContent = (
          <>
            <Controller
              control={formProps.control}
              name="wishlistName"
              rules={{ required: true }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  fullWidth
                  label={t("editWishlistNamePopup.inputLabel")}
                  value={value}
                  onChange={onChange}
                  error={!!error}
                  sx={{
                    marginTop: "8px",
                  }}
                  helperText={error ? t("editWishlistNamePopup.emptyNameError") : t("editWishlistNamePopup.mandatory")}
                />
              )}
            />
          </>
        );
      }

      content = (
        <AtcMainAreaLayout packagingSlot={innestContent} pricingSlot={priceSlot} notificationsSlot={notificationSlot} />
      );
      break;
    }
    case AddToWishlistStep.POST_SENDING_ERROR:
    case AddToWishlistStep.POST_SENDING_SUCCESS: {
      const isNewWishlist = !formProps.watch("wishlistId");
      content = (
        <AtcMainAreaLayout
          packagingSlot={
            <Alert severity={"info"}>
              <Trans i18nKey={`addToWishlist.success.${isNewWishlist ? "new" : "existing"}`}>
                Product has been added to wish list "<b>{{ name: formProps.watch("wishlistName") }}</b>"
              </Trans>
            </Alert>
          }
          pricingSlot={priceSlot}
          notificationsSlot={notificationSlot}
        />
      );
    }
  }

  const terminalState =
    step === AddToWishlistStep.POST_SENDING_ERROR || step === AddToWishlistStep.POST_SENDING_SUCCESS;

  const footer = (
    <>
      <Button variant={"text"} onClick={closePopup}>
        {terminalState ? t("close") : t("cancel")}
      </Button>
      {!terminalState && (
        <Button
          data-test-id={"add-to-wishlist-btn-ok"}
          disabled={status !== "ready"}
          loading={inFlight}
          startIcon={<BookmarkIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
          onClick={formProps.handleSubmit(addToWishlistCb)}
        >
          {t("addToWishlist.add")}
        </Button>
      )}
    </>
  );

  return (
    <ProductDescriptorContext value={product}>
      <AtcDialogShell title={t("page.search.tabular.action.addToWishlist")} onClose={closePopup} footer={footer}>
        <AtcContentLayout info={<AtcProductDetails descriptor={product} packCode={packCode} />} main={content} />
      </AtcDialogShell>
    </ProductDescriptorContext>
  );
};

interface ATWPriceBlockProps {
  descriptor: ProductDescriptor;
  packCode: string;
  priceReference: string;
  quantity: number;
  salePriceEnabled: boolean;
}

const ATWPriceBlock = (props: ATWPriceBlockProps) => {
  const price = getNetPrice({
    // TODO: Need to include cart qty
    totalQuantity: props.quantity,
    product: props.descriptor,
    packCode: props.packCode,
  });

  return <PriceBlock {...props} netPrice={price} selectedParts={[]} />;
};
